import { useTheme } from "@mui/material";
import { PROVIDER_SEARCH_PAGES, TRACK_EVENTS } from "core/consts";
import ConnectedChipGroup, {
  ChipGroupPresenterProps,
} from "ds_legacy/components/ChipGroup";
import RSButton from "ds_legacy/components/RSButton";
import { margin, padding, sizing } from "ds_legacy/materials/metrics";
import {
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_SEMI_BOLD,
} from "ds_legacy/materials/typography";
import { useGetProviderSearchRedirectLink } from "dsl/hooks/useGetProviderSearchRedirectLink";
import { AsteriskExplained } from "dsl/molecules/AsteriskExplained";
import { useProviderSearchPageData } from "dsl/molecules/ProviderSearchAppWrapper/PageMetaData";
import {
  USER_FILTER_WITH_CONSULTANTS,
  USER_FILTER_WITH_PROVIDERS,
} from "dsl/organisms/Filters/consts";
import { ProviderSearchFilters } from "dsl/organisms/Filters/ProviderSearchApp/types";
import { Iterable } from "immutable";
import {
  SimpleFormRenderProps,
  convertModelDefinition,
  valueDef,
} from "react-forms-state";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import {
  FormStepWrapper,
  FunnelFormData,
  FunnelSteps,
  STEP_CONSULTANT_PROVIDER_SELECTION,
  STEP_SOLUTIONS,
  STEP_ZIPCODE,
} from "../common";

const WITH_PROVIDERS = 1;
const WITH_CONSULTANTS = 2;

type ConsultantProviderSelection = Pick<
  ProviderSearchFilters,
  "with_consultants" | "with_providers"
>;

const modelDefinition = convertModelDefinition({
  ...valueDef("consultant_provider_selection", {
    convertIn: (input) => {
      if (!input) return null;
      const inputValue = Iterable.isIterable(input) ? input.toJS() : input;
      const result = [];

      if (inputValue.with_providers) {
        result.push(WITH_PROVIDERS);
      }
      if (inputValue.with_consultants) {
        result.push(WITH_CONSULTANTS);
      }

      return result;
    },
    convertOut: (output) => {
      if (!output) return null;
      const outputValue = Iterable.isIterable(output) ? output.toJS() : output;
      const selectedSearchType: Record<string, boolean> = {};

      selectedSearchType[USER_FILTER_WITH_PROVIDERS] =
        outputValue.includes(WITH_PROVIDERS);
      selectedSearchType[USER_FILTER_WITH_CONSULTANTS] =
        outputValue.includes(WITH_CONSULTANTS);

      return selectedSearchType;
    },
    validate(value, { translations }) {
      const validateValue = Iterable.isIterable(value) ? value.toJS() : value;
      return validateValue?.length
        ? true
        : {
            customMessage:
              translations.providersearch.onboardingFunnel.firstStep.error,
          };
    },
  }),
});

export function ConsultantProviderSelection({
  formData,
  onSubmit,
  setCurrentStep,
  setFormData,
}: {
  formData: FunnelFormData;
  onSubmit: (data: FunnelFormData) => void;
  setCurrentStep: React.Dispatch<React.SetStateAction<FunnelSteps>>;
  setFormData: React.Dispatch<React.SetStateAction<FunnelFormData>>;
}) {
  const { trackEvent } = useTracking();
  const translations = useTranslations();
  const getRedirectLink = useGetProviderSearchRedirectLink();
  const theme = useTheme();

  useProviderSearchPageData(
    PROVIDER_SEARCH_PAGES.ONBOARDING_FUNNEL_SEARCH_TYPE,
  );

  const options: ChipGroupPresenterProps["options"] = [
    {
      name: translations.providersearch.onboardingFunnel.firstStep.providers,
      value: WITH_PROVIDERS,
    },
    {
      name: translations.providersearch.onboardingFunnel.firstStep.consultants,
      value: WITH_CONSULTANTS,
    },
  ];

  return (
    <SimpleFormRenderProps
      asHtmlForm
      formInputValue={{
        // needs to be added manually to match the valueDef, otherwise the input would be null
        consultant_provider_selection: {
          [USER_FILTER_WITH_CONSULTANTS]: formData.with_consultants,
          [USER_FILTER_WITH_PROVIDERS]: formData.with_providers,
        },
      }}
      modelDefinition={modelDefinition}
      onSubmit={({
        consultant_provider_selection: { with_consultants, with_providers },
      }: {
        consultant_provider_selection: ConsultantProviderSelection;
      }) => {
        trackEvent({
          name: TRACK_EVENTS.PROVIDER_SEARCH_FUNNEL_CONSULTANT_PROVIDER_SELECTED,
          with_consultants,
          with_providers,
        });

        if (with_consultants && !with_providers) {
          return onSubmit({ with_consultants, with_providers });
        }

        setFormData({
          ...formData,
          with_consultants,
          with_providers,
        });
        setCurrentStep(STEP_SOLUTIONS);
      }}
    >
      {({ submit }) => {
        return (
          <FormStepWrapper
            formData={formData}
            skipLink={getRedirectLink({
              formData: {
                ...formData,
                with_consultants: undefined,
                with_providers: undefined,
              },
            })}
            step={STEP_CONSULTANT_PROVIDER_SELECTION}
            testId="consultant_provider_selection-select-wrapper"
            title={
              translations.providersearch.onboardingFunnel.firstStep.question
            }
          >
            <>
              <AsteriskExplained
                customMargin={margin(0, 0, 1.5, 0)}
                style={{
                  color: theme.palette.grey[600],
                  fontWeight: FONT_WEIGHT_SEMI_BOLD,
                }}
              />
              <ConnectedChipGroup
                chipStyle={{
                  height: sizing(6),
                  justifyContent: "center",
                  width: "100%",
                }}
                elementName="consultant_provider_selection"
                formGroupSx={{
                  flexDirection: "column",
                  margin: margin(1, 0),
                }}
                formHelperTextSx={{ margin: margin(0.5, 0, 0) }}
                formLabelSx={{
                  fontWeight: FONT_WEIGHT_BOLD,
                }}
                isRequired
                isSquared
                options={options}
                title={
                  translations.providersearch.onboardingFunnel.firstStep
                    .subtitle
                }
              />
              <div
                style={{
                  display: "flex",
                  gap: sizing(2),
                  justifyContent: "center",
                  padding: padding(3, 0, 2),
                  width: "100%",
                }}
              >
                <RSButton
                  buttonWrapperStyle={{
                    width: "100%",
                  }}
                  color="primary"
                  id="go-back-on-consultant-providers-step"
                  loading="na"
                  style={{ margin: margin(0), width: "100%" }}
                  type="button"
                  variant="outlined"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      with_consultants: undefined,
                      with_providers: undefined,
                    });
                    setCurrentStep(STEP_ZIPCODE);
                  }}
                >
                  {translations.providersearch.onboardingFunnel.backButton}
                </RSButton>
                <RSButton
                  buttonWrapperStyle={{
                    width: "100%",
                  }}
                  color="primary"
                  id="confirm-consultants-providers"
                  loading="na"
                  style={{ margin: margin(0), width: "100%" }}
                  type="submit"
                  variant="contained"
                  onClick={submit}
                >
                  {translations.providersearch.onboardingFunnel.nextButton}
                </RSButton>
              </div>
            </>
          </FormStepWrapper>
        );
      }}
    </SimpleFormRenderProps>
  );
}
